import type { CSSProperties } from 'react';
import { colors } from '@tabbio/tokens';

const CONFETTI_COLORS = [
  colors.primary,
  colors.secondary,
  colors.success,
  colors.warning,
  '#94A3B8',
];

type ConfettiPieceConfig = {
  id: number;
  side: 'left' | 'right';
  x: number;
  y: number;
  spin: number;
  delay: number;
  size: number;
  kind: 'dot' | 'dash';
  peakOpacity: number;
  drift: number;
};

const CONFETTI_PIECES: ConfettiPieceConfig[] = Array.from({ length: 24 }, (_, index) => {
  const side = index % 2 === 0 ? 'left' : 'right';
  const direction = side === 'left' ? 1 : -1;
  const pair = Math.floor(index / 2);
  const lane = pair % 6;
  const wave = Math.floor(pair / 6);
  const alternate = lane % 2 === 0 ? 1 : -1;

  return {
    id: index,
    side,
    x: direction * (26 + lane * 15 + wave * 12),
    y: 54 + lane * 24 + (lane % 2) * 10 + wave * 30,
    spin: alternate * (78 + lane * 22 + wave * 32),
    delay: wave * 340 + lane * 48 + (side === 'right' ? 18 : 0),
    size: 3 + ((lane + wave) % 3),
    kind: (index + lane) % 3 === 0 ? 'dot' : 'dash',
    peakOpacity: 0.62 + (lane % 3) * 0.08,
    drift: direction * (5 + (lane % 3) * 2),
  };
});

export function OnboardingConfetti() {
  return (
    <>
      <div className="tabbio-onboarding-confetti" aria-hidden>
        {CONFETTI_PIECES.map((piece) => {
          const isDash = piece.kind === 'dash';
          return (
            <span
              key={piece.id}
              className={`tabbio-onboarding-confetti-piece is-${piece.side}`}
              style={
                {
                  '--confetti-x': `${piece.x}px`,
                  '--confetti-y': `${piece.y}px`,
                  '--confetti-r': `${piece.spin}deg`,
                  '--confetti-delay': `${piece.delay}ms`,
                  '--confetti-color': CONFETTI_COLORS[piece.id % CONFETTI_COLORS.length],
                  '--confetti-w': `${isDash ? piece.size * 2.5 : piece.size}px`,
                  '--confetti-h': `${piece.size}px`,
                  '--confetti-radius': `${isDash ? piece.size / 2 : 999}px`,
                  '--confetti-peak': piece.peakOpacity,
                  '--confetti-mid': piece.peakOpacity * 0.7,
                  '--confetti-drift': `${piece.drift}px`,
                } as CSSProperties
              }
            />
          );
        })}
      </div>
      <style>{`
        .tabbio-onboarding-confetti {
          position: absolute;
          inset: 0;
          z-index: 10;
          pointer-events: none;
          overflow: hidden;
          contain: layout paint;
        }

        .tabbio-onboarding-confetti-piece {
          position: absolute;
          top: 28px;
          width: var(--confetti-w);
          height: var(--confetti-h);
          border-radius: var(--confetti-radius);
          background: var(--confetti-color);
          opacity: 0;
          transform: translate3d(0, 0, 0) rotate(0deg) scale(0.72);
          animation: tabbio-confetti-edge 2200ms cubic-bezier(0.22, 1, 0.36, 1) var(--confetti-delay) forwards;
          will-change: transform, opacity;
        }

        .tabbio-onboarding-confetti-piece.is-left {
          left: 9%;
        }

        .tabbio-onboarding-confetti-piece.is-right {
          right: 9%;
        }

        @keyframes tabbio-confetti-edge {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0) rotate(0deg) scale(0.72);
          }
          14% {
            opacity: var(--confetti-peak);
          }
          76% {
            opacity: var(--confetti-mid);
            transform: translate3d(calc(var(--confetti-x) + var(--confetti-drift)), calc(var(--confetti-y) * 0.76), 0) rotate(calc(var(--confetti-r) * 0.76)) scale(0.92);
          }
          100% {
            opacity: 0;
            transform: translate3d(var(--confetti-x), calc(var(--confetti-y) + 38px), 0) rotate(var(--confetti-r)) scale(0.84);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .tabbio-onboarding-confetti {
            display: none;
          }
        }
      `}</style>
    </>
  );
}
